import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService, ResetPasswordRequest } from '../services/auth';
import { useAuthForm } from '../hooks/useAuthForm';
import { parseAuthError } from '../utils/authUtils';
import AuthPageLayout from '../components/ui/AuthPageLayout';
import PasswordField from '../components/ui/PasswordField';
import AlertMessage from '../components/ui/AlertMessage';
import SubmitButton from '../components/ui/SubmitButton';
import AuthLink from '../components/ui/AuthLink';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const {
    formData,
    handleInputChange,
    isLoading,
    setIsLoading,
    error,
    setError,
    success,
    setSuccess,
  } = useAuthForm({
    password: '',
  });

  useEffect(() => {
    if (!token) {
      setError('Invalid reset token. Please request a new password reset.');
    }
  }, [token, setError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      setError('Invalid reset token. Please request a new password reset.');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');
    setFieldErrors({});

    try {
      const resetData: ResetPasswordRequest = {
        token,
        password: formData.password,
      };

      await authService.resetPassword(resetData);
      setSuccess('Password reset successfully! Redirecting to sign in...');
      setTimeout(() => {
        navigate('/signin');
      }, 2000);
    } catch (err: any) {
      const errorMessage = parseAuthError(
        err,
        'Failed to reset password. Please try again or request a new reset link.'
      );
      setError(errorMessage);

      // Extract field-level errors from the response
      if (err.response?.data?.field_errors) {
        setFieldErrors(err.response.data.field_errors);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageLayout
      title="Set new password"
      description="Enter your new password below."
    >
      <form className="space-y-6" onSubmit={handleSubmit}>
        <AlertMessage type="error" message={error} show={!!error} />
        <AlertMessage type="success" message={success} show={!!success} />

        <PasswordField
          id="password"
          name="password"
          label="New Password"
          placeholder="Enter your new password"
          value={formData.password}
          onChange={handleInputChange}
          required
          autoComplete="new-password"
          disabled={!token}
          serverErrors={fieldErrors.password}
        />

        <SubmitButton isLoading={isLoading} disabled={!token || success !== ''}>
          Reset password
        </SubmitButton>

        <div className="text-center">
          <AuthLink to="/signin">← Back to sign in</AuthLink>
        </div>
      </form>
    </AuthPageLayout>
  );
};

export default ResetPassword;
